import { Injectable, signal, computed } from '@angular/core';
import { ConsentStartResponse, ValidateResponse } from '../models/mandate.model';

const EXPIRY_CHECK_INTERVAL = 30_000; // 30 seconds

interface SessionData {
  requestId: string;
  sessionToken: string;
  sessionExpiresOn: string;
  validateResponse: ValidateResponse;
}

@Injectable({
  providedIn: 'root',
})
export class SessionService {
  // In-memory only — nothing persisted to storage
  private sessionDataSignal = signal<SessionData | null>(null);
  private pendingConsentResultSignal = signal<ConsentStartResponse | null>(null);
  private expiryTimer: ReturnType<typeof setInterval> | null = null;

  readonly sessionData = this.sessionDataSignal.asReadonly();
  readonly validateResponse = computed(() => this.sessionDataSignal()?.validateResponse ?? null);
  readonly requestId = computed(() => this.sessionDataSignal()?.requestId ?? null);
  // Hand-off of a consent/start result from the additional-properties step to the signing page,
  // so /signing can resume the flow (redirect or decoupled) without re-calling /consent/start.
  readonly pendingConsentResult = this.pendingConsentResultSignal.asReadonly();

  setPendingConsentResult(result: ConsentStartResponse | null): void {
    this.pendingConsentResultSignal.set(result);
  }

  constructor() {
    this.startExpiryCheck();
  }

  getSessionToken(): string | null {
    const data = this.sessionDataSignal();
    if (!data || !this.isSessionValid()) return null;
    return data.sessionToken;
  }

  isSessionValid(): boolean {
    const data = this.sessionDataSignal();
    if (!data?.sessionToken || !data?.sessionExpiresOn) return false;
    return new Date(data.sessionExpiresOn) > new Date();
  }

  hasSession(): boolean {
    return this.sessionDataSignal() !== null;
  }

  storeSession(requestId: string, urlToken: string, response: ValidateResponse): void {
    const data: SessionData = {
      requestId,
      sessionToken: response.sessionToken,
      sessionExpiresOn: response.sessionExpiresOn,
      validateResponse: response,
    };
    this.sessionDataSignal.set(data);
    this.startExpiryCheck();
  }

  clearSession(): void {
    this.sessionDataSignal.set(null);
    this.pendingConsentResultSignal.set(null);
    this.stopExpiryCheck();
  }

  private startExpiryCheck(): void {
    this.stopExpiryCheck();
    this.expiryTimer = setInterval(() => {
      if (this.hasSession() && !this.isSessionValid()) {
        this.clearSession();
      }
    }, EXPIRY_CHECK_INTERVAL);
  }

  private stopExpiryCheck(): void {
    if (this.expiryTimer) {
      clearInterval(this.expiryTimer);
      this.expiryTimer = null;
    }
  }
}
